import { NotificationType } from '../../types';

export const MISC_KEY = 'misc';

export type NotificationGroup = {
    key: string,
    orderOrJobId: number | null,
    isMisc: boolean,
    isMyOrder: boolean,
    isViewable: boolean,
    notifications: NotificationType[],
    totalCount: number,
    unreadCount: number,
    latestCreatedAt: string,
    latestActivityMs: number,
    pickupCity: string | null,
    deliveryCity: string | null,
    tenantName: string | null,
};

export type NotificationSort = 'orderIdDesc' | 'latestActivity' | 'unreadFirst';

function toMillis(value: unknown): number {
    if (value instanceof Date) {
        const ms = value.getTime();
        return Number.isFinite(ms) ? ms : 0;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
    }
    if (typeof value === 'string' && value.trim()) {
        const ms = Date.parse(value);
        return Number.isNaN(ms) ? 0 : ms;
    }
    return 0;
}

export function parseOrderIdFromMessage(message: string): number | null {
    const match = message.match(/uppdrag\s+#(\d+)/i);
    if (!match) {
        return null;
    }
    const id = Number(match[1]);
    return Number.isFinite(id) ? id : null;
}

function groupKey(notification: NotificationType): string {
    const id = notification.orderId ?? notification.jobId;
    return id == null ? MISC_KEY : String(id);
}

function compareCreatedAtDesc(a: NotificationType, b: NotificationType) {
    return toMillis(b.createdAt) - toMillis(a.createdAt);
}

export function buildNotificationGroups(
    notifications: NotificationType[]
): NotificationGroup[] {
    const map = new Map<string, NotificationType[]>();

    notifications.forEach((notification) => {
        const key = groupKey(notification);
        const list = map.get(key);
        if (list) {
            list.push(notification);
        } else {
            map.set(key, [notification]);
        }
    });

    return Array.from(map.entries()).map(([key, items]) => {
        const sorted = [...items].sort(compareCreatedAtDesc);
        const isMisc = key === MISC_KEY;
        const orderOrJobId = isMisc ? null : Number(key);
        const withCities = sorted.find((n) => n.pickupCity || n.deliveryCity);
        const withTenant = sorted.find((n) => n.tenantName);

        return {
            key,
            orderOrJobId,
            isMisc,
            isMyOrder: sorted.some((n) => n.isMyOrder),
            isViewable: sorted.some((n) => n.isViewable),
            notifications: sorted,
            totalCount: sorted.length,
            unreadCount: sorted.filter((n) => !n.isRead).length,
            latestCreatedAt: sorted[0].createdAt,
            latestActivityMs: toMillis(sorted[0].createdAt),
            pickupCity: withCities?.pickupCity ?? null,
            deliveryCity: withCities?.deliveryCity ?? null,
            tenantName: withTenant?.tenantName ?? null
        };
    });
}

export function formatGroupRoute(group: NotificationGroup): string | null {
    if (!group.pickupCity && !group.deliveryCity) {
        return null;
    }
    return `${group.pickupCity || '—'} – ${group.deliveryCity || '—'}`;
}

export function filterNotificationGroups(
    groups: NotificationGroup[],
    search: string
): NotificationGroup[] {
    const q = search.trim().toLowerCase();
    if (!q) {
        return groups;
    }

    return groups.filter((group) => {
        if (group.isMisc && 'övrigt'.includes(q)) {
            return true;
        }
        if (group.orderOrJobId != null && String(group.orderOrJobId).includes(q)) {
            return true;
        }
        if (group.isMyOrder && 'mitt uppdrag'.includes(q)) {
            return true;
        }
        if (group.tenantName?.toLowerCase().includes(q)) {
            return true;
        }
        if (group.pickupCity?.toLowerCase().includes(q)) {
            return true;
        }
        if (group.deliveryCity?.toLowerCase().includes(q)) {
            return true;
        }
        return group.notifications.some((n) => (
            n.message.toLowerCase().includes(q)
            || n.pickupCity?.toLowerCase().includes(q)
            || n.deliveryCity?.toLowerCase().includes(q)
        ));
    });
}

function compareLatestActivity(a: NotificationGroup, b: NotificationGroup): number {
    const byTime = b.latestActivityMs - a.latestActivityMs;
    if (byTime !== 0) return byTime;
    // Stable-ish tie-break: real uppdrag before Övrigt, then higher id
    if (a.isMisc !== b.isMisc) return a.isMisc ? 1 : -1;
    return (b.orderOrJobId ?? 0) - (a.orderOrJobId ?? 0);
}

export function sortNotificationGroups(
    groups: NotificationGroup[],
    sort: NotificationSort
): NotificationGroup[] {
    const copy = groups.slice();

    if (sort === 'latestActivity') {
        copy.sort(compareLatestActivity);
        return copy;
    }

    if (sort === 'unreadFirst') {
        copy.sort((a, b) => {
            const aUnread = a.unreadCount > 0 ? 1 : 0;
            const bUnread = b.unreadCount > 0 ? 1 : 0;
            if (aUnread !== bUnread) return bUnread - aUnread;
            const byUnreadCount = b.unreadCount - a.unreadCount;
            if (byUnreadCount !== 0) return byUnreadCount;
            return compareLatestActivity(a, b);
        });
        return copy;
    }

    copy.sort((a, b) => {
        if (a.isMisc && !b.isMisc) return 1;
        if (!a.isMisc && b.isMisc) return -1;
        const byId = (b.orderOrJobId ?? 0) - (a.orderOrJobId ?? 0);
        if (byId !== 0) return byId;
        return b.latestActivityMs - a.latestActivityMs;
    });
    return copy;
}
